<template>
    <div class="ProjectForm">
        <DashboardEditSection title="Information">
            <FormSelectClient
                v-model.number="$v.client.$model"
                :errors="$v.client.$error ? [
                    !$v.client.required && 'Client is required',
                ] : null"
            />
            <FormInput
                v-model="$v.name.$model"
                lazy
                label="Project Name"
                :errors="$v.name.$error ? [
                    !$v.name.required && 'Project name is required',
                ] : null"
            />
            <AddressForm
                :address.sync="address"
                :city.sync="city"
                :state.sync="state"
                :zip.sync="zip"
            />

            <FormInput v-model="loadSite" label="Load Site Name"/>
            <FormInput v-model="dumpSite" label="Dump Site Name"/>

            <FormTextarea v-model="notes" label="Notes"/>
        </DashboardEditSection>

        <DashboardEditSection title="Materials">
            <div class="toggleRow">
                <h4>Restrict materials</h4>
                <FormToggle
                    v-model="isMaterialsRestricted"
                    @input="handleToggleRestrictMaterials"
                />
            </div>
            <ProjectMaterialSelector
                v-show="isMaterialsRestricted"
                ref="projectMaterialSelector"
                :materials.sync="materials"
            />
        </DashboardEditSection>

        <DashboardEditSection title="Pricing" class="pricingSection">
            <FormSelect v-model="rateType" label="Rate Type" class="rateTypeSelector">
                <option value="hourly">Hourly</option>
                <option value="material">By Material</option>
            </FormSelect>

            <div v-if="rateType === 'hourly'" class="pricingType">
                <Row>
                    <Column :md="4">
                        <FormCurrency v-model="hourlyRate" label="Hourly Rate" :min="0"/>
                    </Column>
                </Row>
                <ProjectHourlyRateOptions :hourlyRateOptions.sync="hourlyRateOptions"/>
            </div>
            <div v-else class="pricingType">
                <ProjectMaterialList :materialRates.sync="materialRates"/>
                <ProjectHourlyRateOptions :hourlyRateOptions.sync="hourlyRateOptions" isMaterial/>
            </div>

            <!-- <h2>Mobilization &amp; Demobilization</h2>
            <p>Disclaimer: Mobilization and Demobilization prices represent the total price for the entire Project.</p>
            <Row>
                <Column :md="6">
                    <FormCurrency v-model="mobilizationPrice" label="Mobilization Price" :min="0"/>
                </Column>
                <Column :md="6">
                    <FormCurrency v-model="demobilizationPrice" label="Demobilization Price" :min="0"/>
                </Column>
            </Row> -->
        </DashboardEditSection>

        <DashboardEditSection title="Reporting">
            <div class="toggleRow">
                <h4>Time overrides</h4>
                <FormToggle
                    v-model="isTimeOverridesEnabled"
                    @input="handleToggleTimeOverrides"
                />
            </div>
            <ProjectReportTimeOverrides
                v-show="isTimeOverridesEnabled"
                ref="projectReportTimeOverrides"
                :reportTimeOverrides.sync="reportTimeOverrides"
            />
        </DashboardEditSection>

        <DashboardEditSection title="Contact">
            <Row>
                <Column :md="6">
                    <FormInput v-model="firstName" label="First Name"/>
                </Column>
                <Column :md="6">
                    <FormInput v-model="lastName" label="Last Name"/>
                </Column>
            </Row>
            <FormInput v-model="email" label="Email"/>
            <FormInput v-model="phone" label="Phone"/>
        </DashboardEditSection>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import DashboardEditSection from '@/components/DashboardEditSection';
import AddressForm from '@/components/form/AddressForm';
import ProjectMaterialSelector from './ProjectMaterialSelector';
import ProjectMaterialList from './ProjectMaterialList';
import ProjectHourlyRateOptions from './ProjectHourlyRateOptions';
import ProjectReportTimeOverrides from './ProjectReportTimeOverrides';

export default {
    name: 'ProjectForm',

    components: {
        DashboardEditSection,
        AddressForm,
        ProjectMaterialSelector,
        ProjectMaterialList,
        ProjectHourlyRateOptions,
        ProjectReportTimeOverrides,
    },

    data: () => ({
        isMaterialsRestricted: false,
        isTimeOverridesEnabled: false,
    }),

    syncProps: [
        'client',
        'name',
        'address',
        'city',
        'state',
        'zip',
        'firstName',
        'lastName',
        'email',
        'phone',
        'rateType',
        'hourlyRate',
        'hourlyRateOptions',
        'reportTimeOverrides',
        'materialRates',
        'mobilizationPrice',
        'demobilizationPrice',
        'loadSite',
        'dumpSite',
        'notes',
        // Relations
        'materials',
    ],

    validations: {
        client: {
            required,
        },
        name: {
            required,
        },
    },

    methods: {
        validate() {
            this.$v.$touch();
            return !this.$v.$invalid;
        },

        handleToggleRestrictMaterials(isEnabled) {
            // Clear restricted materials when toggle is off
            if (!isEnabled) {
                this.$refs.projectMaterialSelector.clearAllMaterials();
            }
        },

        handleToggleTimeOverrides(isEnabled) {
            // Clear report time overrides when toggle is off
            if (!isEnabled) {
                this.$refs.projectReportTimeOverrides.clearAllOverrides();
            }
        },
    },

    mounted() {
        this.isMaterialsRestricted = Boolean(this.materials?.length);

        const overrides = this.reportTimeOverrides;
        this.isTimeOverridesEnabled = overrides && Object.values(overrides).some(value => value > 0);
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';
.ProjectForm {

    .toggleRow {
        margin-bottom: 30px;
    }

    .rateTypeSelector {
        margin-bottom: 0;

        ::v-deep select {
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
        }
    }

    .pricingSection {
        h2 {
            margin-bottom: 5px;
        }

        p {
            margin-bottom: $grid-gutter-width;
        }
    }

    .pricingType {
        border-bottom-left-radius: $border-radius;
        border-bottom-right-radius: $border-radius;
        border: 1px solid $divider-color;
        border-top: none;
        padding: $grid-gutter-width;
        margin-bottom: 30px;
    }
}
</style>
